export type UserRole = "admin" | "user";
export type UserStatus = "active" | "inactive";
export type SeniorityLevel = "intern" | "fresher" | "junior" | "middle" | "senior" | "manager" | "director" | "ceo";
export type Department = "ceo" | "product" | "engineering" | "operations" | "accounting" | "cs" | "growth_marketing" | "hr" | "finance";
export type PriorityType = "SHP" | "WKR" | "DAS";
export type PriorityStatus = "focus" | "queue" | "done" | "blocked" | "on-hold" | "waiting";
export type SortOrder = "ASC" | "DESC";

export interface User {
  pid: string;
  email: string;
  fullName: string;
  avatarUrl?: string;
  position?: string;
  seniorityLevel?: SeniorityLevel;
  department?: Department;
  joinDate?: Date;
  status: UserStatus;
  createdAt: Date;
  updatedAt: Date;
  company?: Company;
  role?: Role;
  teams?: Team[];
  teamPid?: string;
  managerPid?: string;
  manager?: {
    pid: string;
    fullName: string;
    avatarUrl?: string;
  };
  isLeader?: boolean;
}

export interface Company {
  name: string;
}

export interface Team {
  pid: string;
  name: string;
  description?: string;
}

export class Role {
  pid: string;
  name: UserRole;
}

export interface TeamMember {
  pid: string;
  email: string;
  fullName: string;
  avatarUrl?: string;
}

// ============================================
// Organizational Chart Types
// ============================================

/**
 * Organizational hierarchy node representing a single member
 * with their position in the org structure
 */
export interface OrgNode {
  pid: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
  position?: string;
  department?: string;
  managerPid?: string;
  
  // Computed fields for org chart
  directReports: OrgNode[];
  reportCount: number;
  hierarchyLevel: number; // 0 = CEO, 1 = direct reports to CEO, etc.
}

/**
 * Flattened org member for search/filter operations
 */
export interface OrgMember {
  pid: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
  position?: string;
  department?: string;
  managerPid?: string;
  managerName?: string;
  reportCount: number;
}

/**
 * API Response for org hierarchy endpoint
 */
export interface OrgHierarchyResponse {
  root: OrgNode;
  totalMembers: number;
  maxDepth: number;
  departments: string[];
}

/**
 * Weekly-update health of one team member, as the backend states it.
 * `on_track` requires BOTH halves: an update posted this week AND a live Focus task.
 *
 * Domain states, not colours — the backend deliberately stopped sending `"green"`/`"red"` so a
 * palette change would not be a contract change. This page does not render this field: it derives its
 * own tone from `updatedThisWeek` and `lastUpdateAt` instead (see `UpdateTone` in
 * `components/team/teamFilterConstants.ts`), because it distinguishes three degrees of staleness where
 * this collapses to two.
 */
export type TeamUpdateStatusLevel = "on_track" | "needs_update";

export interface TeamMemberUpdateStatus {
  pid: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
  position?: string;
  /** True for the row belonging to the viewing user. */
  isSelf: boolean;
  status: TeamUpdateStatusLevel;
  updatedThisWeek: boolean;
  hasFocusTask: boolean;
  /** ISO timestamp of the most recent report ever posted; absent if they never reported. */
  lastUpdateAt?: string;
  /**
   * ISO timestamp of the most recent priority ACTIVITY (report, create/edit priority, status
   * change) from the activity log. Optional: absent until the backend ships it or the member has
   * no logged activity — display falls back to `lastUpdateAt`.
   */
  lastActivityAt?: string;
  /**
   * Current Focus/Queue counts against the 1 Focus + 2 Queue board-completeness rule. Optional: the
   * backend may not send them yet, in which case the flag stays hidden rather than rendering a
   * false "missing" state (see `isMissingFocusQueue` / `missingFocusQueueLabel`).
   */
  focusCount?: number;
  queueCount?: number;
}

export interface TeamUpdateStatusResponse {
  weekNumber: number;
  weekYear: number;
  weekStart: string;
  weekEnd: string;
  members: TeamMemberUpdateStatus[];
}

/**
 * Manager assignment update payload
 */
export interface UpdateManagerPayload {
  userPid: string;
  newManagerPid: string | null; // null to make user a top-level employee
}

/**
 * Org chart view state (for persistence in sessionStorage)
 */
export interface OrgChartViewState {
  zoom: number; // 0.5 to 2.0
  pan: { x: number; y: number };
  collapsedNodes: string[]; // Array of collapsed node PIDs
  focusedNode: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
}

export interface Priority {
  pid: string;
  code: string;
  type: PriorityType;
  name: string;
  description?: string;
  status: PriorityStatus;
  weekNumber?: number;
  weekYear?: number;
  assignments?: PriorityAssignment[];
  createdBy?: string; // User PID
  /** DRI (Directly Responsible Individual) when API embeds separately from assignments */
  dri?: AssignedUser;
  blocker?: AssignedUser; // Blocker (optional, required when status is blocked)
  isCreator?: boolean;
  isConfidential?: boolean;
  /** True when a linked PPP is confidential, even if this row's own flag is false */
  inheritsConfidential?: boolean;
  /** Linked PPPs; deliverable order is derived from the best-ranked one */
  ppps: PriorityPppRef[];
  /** Derived by BE: no PPP link or no DRI assignment */
  isCandidate: boolean;
  /** ISO timestamps; absent on older BE builds */
  createdAt?: string;
  updatedAt?: string;
}

/** `POST /api/priorities/:pid/assignments/me` — join a candidate; `asDri: true` claims it. */
export interface IClaimAssignment {
  asDri?: boolean;
  status?: PriorityStatus;
  icTask?: string;
}

/** Request row for create/update — matches BE `PriorityAssignmentFieldsReqDTO` */
export interface PriorityAssignmentInput {
  userPid: string;
  status: PriorityStatus;
  icTask: string; // max 2000 on BE; empty string is valid
  /** Marks this assignee as the DRI (Directly Responsible Individual) */
  isDri: boolean;
  /** UUID of blocker assignee, or empty string to clear */
  blockerUserPid?: string;
  /** Free-text blocker reason (max 4000 chars) */
  blockedReason?: string;
}

export interface PriorityAssignment {
  assignedUser: AssignedUser;
  assignedAt: Date;
  /** Per-assignee workflow status (rollup uses same enum as priority-level status) */
  status?: PriorityStatus | null;
  icTask?: string | null;
  /** True when this assignee is the DRI for this priority */
  isDri?: boolean;
  blocker?: AssignedUser;
  blockedReason?: string | null;
}

export interface AssignedUser {
  pid: string;
  fullName: string;
  avatarUrl?: string;
}

// ── PPP (Product / Partner / Project) — the only hand-ranked layer ──
export type PppType = "product" | "partner" | "project";
export type PppCategory = "fintech" | "ai" | "company";

/** Whitelisted viewer of a confidential PPP — same shape as `KrContributor`. */
export type PppMember = KrContributor;

export interface PppKeyResultRef {
  pid: string;
  code: string;
  name: string;
}

export interface Ppp {
  pid: string;
  name: string;
  description?: string | null;
  type: PppType;
  category?: PppCategory | null;
  orderIndex: number;
  dri?: AssignedUser | null;
  keyResults: PppKeyResultRef[];
  /** Linked deliverables that have a DRI (candidates excluded) */
  deliverableCount: number;
  isConfidential: boolean;
  /** Non-leaders whitelisted to see a confidential PPP */
  members: PppMember[];
  createdAt: string;
  updatedAt: string;
}

export interface PppFilter {
  type?: PppType;
  category?: PppCategory;
  keyResultPid?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: SortOrder;
  page?: number;
  limit?: number;
}

export interface IUpsertPpp {
  name: string;
  /** `null` clears an existing description */
  description?: string | null;
  type: PppType;
  category?: PppCategory | null;
  /** Must contain at least one KR */
  keyResultPids: string[];
  driUserPid?: string | null;
  isConfidential?: boolean;
  /** Replaces the whole member whitelist */
  memberPids?: string[];
}

export interface PppReorderItem {
  pid: string;
  orderIndex: number;
}

/** PPP as embedded on a Priority — `orderIndex` drives derived ordering/grouping */
export interface PriorityPppRef {
  pid: string;
  name: string;
  type: PppType;
  orderIndex: number;
  isConfidential: boolean;
}

export interface PriorityFilter {
  search?: string;
  type?: string;
  status?: string;
  userPid?: string;
  teamPid?: string;
  pppPids?: string[];
  candidate?: boolean;
}

export interface ICreatePriority {
  name: string;
  type: PriorityType;
  description?: string;
  weekNumber?: number;
  weekYear?: number;
  /** One assignment should have isDri: true to designate the DRI */
  assignments?: PriorityAssignmentInput[];
  /** Full-replace when present; `[]` unlinks all PPPs */
  pppPids?: string[];
  blockerUserPid?: string; // Optional: Blocker
  isConfidential?: boolean;
}

export interface IUpdatePriority {
  name?: string;
  type?: PriorityType;
  /** Optional manual status update on PATCH */
  status?: PriorityStatus;
  description?: string;
  weekNumber?: number;
  weekYear?: number;
  /** Full-replace when present; `[]` clears all assignees. One should have isDri: true */
  assignments?: PriorityAssignmentInput[];
  /** Full-replace when present; `[]` unlinks all PPPs */
  pppPids?: string[];
  blockerUserPid?: string; // Optional: Blocker
  isConfidential?: boolean;
}

/**
 * Body for `PATCH /api/priorities/:pid/assignments/me` — the current user's own assignment row.
 *
 * No `userPid`: the subject is the caller. This is what replaced sending the whole team's rows back
 * (`IUpdatePriority.assignments`) just to change one field on one of them.
 */
export interface IUpdateOwnAssignment {
  status?: PriorityStatus;
  icTask?: string;
  /** Required when moving to `blocked` and no blocker is already recorded. */
  blockerUserPid?: string;
  blockedReason?: string;
}

// ============================================
// Accountability page types
// ============================================

/** Matches BE's embedded user shape on `GET /api/priorities/accountability` rows. */
export interface AccountabilityUser {
  pid: string;
  fullName: string;
  avatarUrl?: string;
  position?: string;
}

/** One task row under a DRI on the accountability page. */
export interface AccountabilityTask {
  pid: string;
  code: string;
  name: string;
  status: PriorityStatus;
}

/** One person ranked by how many tasks they are DRI on. */
export interface AccountabilityDriEntry {
  user: AccountabilityUser;
  taskCount: number;
  tasks: AccountabilityTask[];
}

/** One task someone else is blocked on, under a blocker's row. */
export interface AccountabilityBlockedTask {
  pid: string;
  code: string;
  name: string;
  status: PriorityStatus;
  blockedReason?: string;
  owner: { pid: string; fullName: string };
}

/** One person blocking others, with the tasks they are blocking. */
export interface AccountabilityBlockerEntry {
  user: AccountabilityUser;
  blockedCount: number;
  blockedTasks: AccountabilityBlockedTask[];
}

/** `GET /api/priorities/accountability` response: DRIs ranked + who is blocking whom. */
export interface AccountabilityOverview {
  dris: AccountabilityDriEntry[];
  blockers: AccountabilityBlockerEntry[];
}

export interface Report {
  pid: string;
  user: {
    pid: string;
    fullName: string;
    avatarUrl?: string;
  };
  content: string;
  reportDate: string;
  blocker: boolean;
  blockedReason?: string;
  priorities?: {
    pid: string;
    code: string;
    name: string;
  }[];
  priority?: {
    pid: string;
    code: string;
    name: string;
  };
  createdAt: string;
}

export interface PriorityComment {
  pid: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    pid: string;
    fullName: string;
    avatarUrl?: string;
  };
  isOwner?: boolean;
}

export interface ReportFilter {
  userPid?: string;
  priorityPid?: string;
  blocker?: boolean;
  reportDate?: Date;
}

export interface ObjectiveReorderItem {
  pid: string;
  orderIndex: number;
}

export interface KeyResultReorderItem {
  pid: string;
  orderIndex: number;
}

export interface PriorityOverview {
  total: number;
  totalFocus: number;
  totalQueue: number;
  totalCompleted: number;
}

/** Per-user count of Focus (max 1) and Queue (max 2) priorities, for inline assignment prevention. */
export interface AssignmentLoad {
  userPid: string;
  focus: number;
  queue: number;
}

export interface AdminOverview {
  totalPriorities: number;
  totalActiveUsers: number;
  totalReportsToday: number;
  totalBlockerReportsToday: number;
}

// ============================================
// Admin Message Review Types
// ============================================

export type AdminMessageRole = "user" | "assistant";

export interface AdminMessage {
  pid: string;
  role: AdminMessageRole;
  content: string;
  createdAt: string;
  updatedAt?: string;
  user?: {
    pid: string;
    fullName: string;
    avatarUrl?: string;
  };
}

export interface AdminMessagesResponse {
  data: AdminMessage[];
  total: number;
}

// ============================================
// Copilot Analytics Types
// ============================================

export interface CopilotDailyUsage {
  /** ISO date, `YYYY-MM-DD`. */
  day: string;
  userMessages: number;
  activeUsers: number;
}

export interface CopilotToolUsage {
  /** Empty when the gateway dropped the tool-call start event. */
  toolName: string;
  calls: number;
  /** Calls whose result envelope reported `ok: false`. */
  failures: number;
  /** Calls that never produced a result — the run died mid-flight. */
  missingResults: number;
}

export interface CopilotToolError {
  error: string;
  count: number;
}

export interface CopilotAnalytics {
  days: number;
  totalUserMessages: number;
  totalActiveUsers: number;
  totalToolCalls: number;
  totalToolFailures: number;
  daily: CopilotDailyUsage[];
  tools: CopilotToolUsage[];
  errors: CopilotToolError[];
}

// ============================================
// OKR (Objectives & Key Results) Types
// ============================================

export type OkrCycleStatus = "draft" | "active" | "closed";

/** Owner payload on objective / key result from BE (nested user). */
export interface OkrOwnerUser {
  pid: string;
  email?: string;
  fullName: string;
  avatarUrl?: string;
  position?: string;
  status?: UserStatus;
  createdAt?: string;
  updatedAt?: string;
  isLeader?: boolean;
}

// Raw OKR payload contracts returned by BE
export interface OkrOwnerUserDto {
  pid: string;
  email?: string;
  fullName: string;
  avatarUrl?: string;
  position?: string;
  status?: UserStatus;
  createdAt?: string;
  updatedAt?: string;
  isLeader?: boolean;
}

/** Matches BE `ObjectiveStatus` enum (string values). */
export enum ObjectiveStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ON_HOLD = "on-hold",
}

/** Company OKR lane — objectives are not assigned to teams */
export type ObjectiveCategory = "fintech" | "ai" | "company";

/** Matches BE `KeyResultStatus` enum (same values as objective). */
export enum KeyResultStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  ON_HOLD = "on-hold",
}

export interface OkrCycle {
  pid: string;
  name: string;
  quarter: number;
  year: number;
  startDate: string;
  endDate: string;
  status: OkrCycleStatus;
  createdAt: string;
  updatedAt: string;
}

export interface Objective {
  pid: string;
  code: string;
  name: string;
  description?: string;
  /** Markdown rationale — why this goal now, how the team intends to win, risks. */
  context?: string;
  status: ObjectiveStatus;
  category: ObjectiveCategory;
  progress: number;
  targetProgress: number;
  startDate?: string;
  dueDate?: string;
  orderIndex?: number;
  cycle?: OkrCycle;
  owner?: OkrOwnerUser;
  keyResults?: KeyResult[];
  createdAt: string;
  updatedAt: string;
}

export interface ObjectiveDto {
  pid: string;
  code: string;
  name: string;
  description?: string | null;
  context?: string | null;
  status: ObjectiveStatus;
  category?: string | null;
  progress: number | string;
  targetProgress: number | string;
  startDate?: string | null;
  dueDate?: string | null;
  orderIndex?: number;
  cycle?: OkrCycle;
  owner?: OkrOwnerUserDto;
  keyResults?: KeyResultDto[];
  createdAt: string;
  updatedAt: string;
}

export interface KrContributor {
  pid: string;
  fullName: string;
  avatarUrl?: string | null;
}

export interface KeyResult {
  pid: string;
  code: string;
  name: string;
  description?: string;
  /** Markdown rationale — why this result matters, how the team intends to hit it, risks. */
  context?: string;
  status: KeyResultStatus;
  progress: number;
  targetProgress: number;
  weight?: number;
  startDate?: string;
  dueDate?: string;
  orderIndex?: number;
  /** Parent objective; list endpoint may send only `objectivePid` */
  objectivePid?: string;
  objective?: {
    pid: string;
    code: string;
    name: string;
  };
  owner?: OkrOwnerUser;
  contributors: KrContributor[];
  contributorCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface KeyResultDto {
  pid: string;
  code: string;
  name: string;
  description?: string | null;
  context?: string | null;
  status: KeyResultStatus;
  progress: number | string;
  targetProgress: number | string;
  weight?: number | string;
  startDate?: string | null;
  dueDate?: string | null;
  orderIndex?: number;
  objectivePid?: string | null;
  objective?: {
    pid: string;
    code: string;
    name: string;
  };
  owner?: OkrOwnerUserDto;
  contributors?: KrContributor[];
  contributorCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface EntityResponse<T> {
  data: T;
}

export interface ICreateObjective {
  name: string;
  category?: ObjectiveCategory;
  description?: string;
  context?: string;
  startDate?: string;
  dueDate?: string;
  cyclePid?: string;
  ownerUserPid?: string;
}

export interface IUpdateObjective {
  name?: string;
  category?: ObjectiveCategory;
  description?: string;
  /** `null` clears the stored strategy; omit to keep it. */
  context?: string | null;
  startDate?: string;
  dueDate?: string;
  cyclePid?: string;
  /** `null` clears the owner; omit to keep it. */
  ownerUserPid?: string | null;
  status?: ObjectiveStatus;
}

export interface ICreateKeyResult {
  name: string;
  description?: string;
  context?: string;
  startDate?: string;
  dueDate?: string;
  objectivePid?: string;
  ownerUserPid?: string;
  weight?: number;
  targetProgress?: number;
}

export interface IUpdateKeyResult {
  name?: string;
  description?: string;
  /** `null` clears the stored strategy; omit to keep it. */
  context?: string | null;
  startDate?: string;
  dueDate?: string;
  objectivePid?: string;
  /** `null` clears the owner; omit to keep it. */
  ownerUserPid?: string | null;
  weight?: number;
  progress?: number;
  targetProgress?: number;
  status?: KeyResultStatus;
}

export interface ObjectiveFilter {
  search?: string;
  statuses?: string | string[];
  category?: ObjectiveCategory;
  cyclePid?: string;
  ownerUserPid?: string;
  sortBy?: string;
  sortOrder?: SortOrder;
  page?: number;
  limit?: number;
}

export interface KeyResultFilter {
  search?: string;
  statuses?: string | string[];
  objectivePid?: string;
  ownerUserPid?: string;
  sortBy?: string;
  sortOrder?: SortOrder;
  page?: number;
  limit?: number;
}
